import { useEffect, useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { Building2, LayoutDashboard, LogOut } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import RexpertLogo from "@/components/RexpertLogo";

const navItems = [
  { to: "/", label: "Start" },
  { to: "/rechtliches", label: "Rechtliches" },
  { to: "/faq", label: "FAQ" },
  { to: "/login", label: "Login" },
];

export default function MainLayout({ children, session, onLogout, pageTitle, pageDescription }) {
  const [showLogoPreview, setShowLogoPreview] = useState(false);
  const [cookieAccepted, setCookieAccepted] = useState(() => {
    try {
      return window.localStorage.getItem("reexperts-cookie-consent-v1") === "accepted";
    } catch (error) {
      return false;
    }
  });

  const acceptCookies = () => {
    try {
      window.localStorage.setItem("reexperts-cookie-consent-v1", "accepted");
    } catch (error) {
      // ignore storage error
    }
    setCookieAccepted(true);
  };

  useEffect(() => {
    const fullTitle = pageTitle ? `${pageTitle} | REEXPERTS` : "REEXPERTS – Vereinigung freier Immobiliensachverständiger";
    document.title = fullTitle;

    const description = pageDescription || "REEXPERTS – Vereinigung freier Immobiliensachverständiger";
    const descriptionMeta = document.querySelector('meta[name="description"]');
    if (descriptionMeta) {
      descriptionMeta.setAttribute("content", description);
    }
  }, [pageTitle, pageDescription]);

  return (
    <div className="min-h-screen bg-[#F8F9FA] text-slate-900">
      <header className="sticky top-0 z-50 border-b border-slate-200 bg-white/85 backdrop-blur-xl">
        <div className="h-[3px] w-full bg-[linear-gradient(90deg,#2f2f2f_0%,#2f2f2f_33%,#a8171b_33%,#a8171b_66%,#d4af37_66%,#d4af37_100%)]" data-testid="header-flag-strip" />
        <div className="mx-auto flex w-full max-w-7xl flex-col gap-3 px-4 py-3.5 sm:flex-row sm:items-center sm:justify-between sm:px-6 lg:px-8">
          <button
            type="button"
            onClick={() => setShowLogoPreview(true)}
            className="flex w-full items-center gap-3 sm:w-auto"
            data-testid="main-logo-link"
          >
            <div className="flex items-center gap-3 md:hidden">
              <RexpertLogo compact testId="header-brand-logo-mobile" />
              <div className="flex flex-col gap-1">
                <p className="text-[1.32rem] font-extrabold leading-none tracking-[-0.05em]" data-testid="header-brand-logo-mobile-wordmark"><span className="text-[#2B3037]">RE</span><span className="text-[#A0272B]">EX</span><span className="text-[#2B3037]">PERTS</span></p>
                <div className="flex items-center gap-1.5" data-testid="header-brand-logo-mobile-flag-accent">
                  <span className="h-[3px] w-6 rounded-full bg-[#2f343c]" />
                  <span className="h-[3px] w-6 rounded-full bg-[#a0272b]" />
                  <span className="h-[3px] w-6 rounded-full bg-[#d4af37]" />
                </div>
                <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-[#7A6438]" data-testid="header-brand-logo-mobile-subline">reexperts.de · D-A-CH</p>
              </div>
            </div>
            <div className="hidden md:block">
              <RexpertLogo testId="header-brand-logo" />
            </div>
          </button>

          <nav className="hidden items-center gap-1.5 lg:flex" data-testid="main-navigation">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                data-testid={`nav-link-${item.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                className={({ isActive }) =>
                  `rounded-full px-3.5 py-2 text-sm font-medium transition-colors ${
                    isActive
                      ? "bg-slate-900 text-white"
                      : "text-slate-700 hover:bg-slate-100 hover:text-slate-900"
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex w-full items-center justify-end gap-3 sm:w-auto">
            {session?.user ? (
              <div className="flex items-center gap-2">
                <Link to={session.user.role === "admin" ? "/admin" : "/mitglied"} data-testid="header-dashboard-link">
                  <Button className="rounded-full bg-slate-900 text-white hover:bg-slate-800">
                    <LayoutDashboard className="mr-2 h-4 w-4" /> Bereich öffnen
                  </Button>
                </Link>
                <Button
                  variant="outline"
                  className="rounded-full border-slate-300 bg-white text-slate-900 hover:bg-slate-50"
                  onClick={onLogout}
                  data-testid="header-logout-button"
                >
                  <LogOut className="mr-2 h-4 w-4" /> Abmelden
                </Button>
              </div>
            ) : (
              <Link to="/apply" data-testid="header-apply-link">
                <Button className="rounded-full bg-slate-900 text-white hover:bg-slate-800">
                  <Building2 className="mr-2 h-4 w-4" /> <span className="sm:hidden">Mitglied</span><span className="hidden sm:inline">Mitgliedschaft</span>
                </Button>
              </Link>
            )}
          </div>
        </div>
      </header>

      <main>{children}</main>

      {!cookieAccepted && (
        <div className="fixed inset-x-3 bottom-3 z-[85] rounded-[1rem] border border-slate-200 bg-white p-2.5 shadow-md sm:inset-x-auto sm:left-5 sm:bottom-5 sm:max-w-[290px] sm:rounded-[1.2rem] sm:p-3" data-testid="cookie-consent-banner">
          <Badge className="mb-2 hidden border-slate-200 bg-slate-100 text-slate-700 sm:inline-flex" data-testid="cookie-consent-badge">Hinweis</Badge>
          <p className="text-[11px] leading-relaxed text-slate-700 sm:text-xs" data-testid="cookie-consent-text">
            <span className="sm:hidden">Technisch erforderliche Cookies und Serverhilfen werden für den sicheren Betrieb verwendet.</span>
            <span className="hidden sm:inline">Diese Plattform verwendet technisch erforderliche Cookies und serverbezogene Hilfsmittel für einen sicheren Betrieb.</span>
          </p>
          <div className="mt-2.5 flex items-center justify-between gap-3 sm:mt-3 sm:flex-row sm:flex-wrap sm:items-center">
            <Link to="/rechtliches#cookie-hinweise" className="text-xs font-medium text-slate-900 underline underline-offset-4 sm:text-sm" data-testid="cookie-consent-privacy-link">
              Datenschutz
            </Link>
            <Button className="h-8 rounded-full bg-slate-900 px-3.5 text-xs text-white hover:bg-slate-800 sm:w-auto" onClick={acceptCookies} data-testid="cookie-consent-accept-button">
              Bestätigen
            </Button>
          </div>
        </div>
      )}

      {showLogoPreview && (
        <div className="fixed inset-0 z-[90] flex items-center justify-center bg-slate-950/70 p-6 backdrop-blur-sm" data-testid="logo-preview-overlay">
          <div className="relative w-full max-w-xl rounded-[2rem] border border-white/15 bg-white p-8 shadow-2xl" data-testid="logo-preview-modal">
            <button
              type="button"
              onClick={() => setShowLogoPreview(false)}
              className="absolute right-4 top-4 rounded-full border border-slate-200 px-3 py-1 text-sm text-slate-700 hover:bg-slate-100"
              data-testid="logo-preview-close-button"
            >
              Schließen
            </button>
            <div className="flex flex-col items-center gap-5 text-center">
              <img src="/reexperts-seal-gemini.png" alt="REEXPERTS Siegel groß" className="h-[220px] w-[220px] object-contain" data-testid="logo-preview-image" />
              <div>
                <p className="font-mono text-xs uppercase tracking-[0.28em] text-[#8A6F3B]" data-testid="logo-preview-kicker">Vereinigung freier Immobiliensachverständiger</p>
                <p className="mt-3 text-[2.4rem] font-extrabold tracking-[-0.05em]" data-testid="logo-preview-wordmark"><span className="text-[#2B3037]">RE</span><span className="text-[#A0272B]">EX</span><span className="text-[#2B3037]">PERTS</span></p>
              </div>
            </div>
          </div>
        </div>
      )}

      <footer className="border-t border-slate-200 bg-slate-950 text-[#f5efe4]">
        <div className="mx-auto grid max-w-7xl gap-6 px-6 py-11 lg:grid-cols-[1.5fr_1fr_1fr] lg:px-8">
          <div>
            <p className="font-mono text-xs uppercase tracking-[0.3em] text-[#b79f73]" data-testid="footer-kicker">
              REEXPERTS Qualitätsstandard · D-A-CH
            </p>
            <h3 className="mt-3 max-w-xl font-serif text-3xl leading-tight" data-testid="footer-heading">
              Für Autorität, saubere Prüfung und sichtbare Qualität im Immobilien-Sachverständigenwesen.
            </h3>
          </div>
          <div>
            <p className="text-sm font-semibold text-white" data-testid="footer-contact-heading">Kontakt</p>
            <p className="text-sm text-[#9f988d]" data-testid="footer-contact-note">Admin, Mitgliedsprüfung, Verbandskommunikation</p>
          </div>
          <div>
            <p className="text-sm font-semibold text-white" data-testid="footer-links-heading">Direktzugänge</p>
            <div className="mt-2 flex flex-col gap-2 text-sm text-[#ddd6cb]">
              <Link to="/apply" className="hover:text-white" data-testid="footer-apply-link">Mitgliedsantrag</Link>
              <Link to="/faq" className="hover:text-white" data-testid="footer-faq-link">FAQ</Link>
              <Link to="/rechtliches" className="hover:text-white" data-testid="footer-legal-link">Rechtliche Grundlagen</Link>
              <Link to="/begriffsverzeichnis" className="hover:text-white" data-testid="footer-glossary-link">Begriffsverzeichnis</Link>
              <Link to="/beschwerde" className="hover:text-white" data-testid="footer-complaint-link">Beschwerdestelle</Link>
              <Link to="/login" className="hover:text-white" data-testid="footer-login-link">Login</Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
